import {localization} from "@/common/lang/localization";
import {enumDef} from "@/common/enumDefs";
import Big from "big.js";

const localizedNotAvailableText = localization.i18n.t("common.value.notAvailable");
const localizedHiddenValue = localization.i18n.t("common.value.hidden");

/**
 * Formats a date based on the current user language.
 */
function formatAsLocalizedDate(value, params = {}) {
    const defaults = {
        showEmptyAsNotAvailable: false,
        showTime: false,
        showDaysLeft: false,
        valueFormat: "yyyy-MM-dd"
    };
    params = { ...defaults, ...params };

    if (!isValueSet(value)) {
        return params.showEmptyAsNotAvailable ? localizedNotAvailableText : "";
    }

    let localizedDate = moment(value, toMomentDateFormat(params.valueFormat)).format(params.outputFormat ? params.outputFormat :(params.showTime ? "L HH:mm" : "L"));

    if (params.showDaysLeft) {
        let today = moment().startOf('day')
        let date = moment(value).startOf('day');
        let remainingDays = date.diff(today, 'days');

        localizedDate = localization.i18n.t("common.value.dateWithRemainingDays", {
            date: localizedDate,
            remainingDays: remainingDays
        });
    }

    return localizedDate;
}

/**
 * Formats a date for communication with the backend API.
 */
function formatAsDateForAPI(value, params = {}) {
    const defaults = {
        withTime: false,
    };
    params = { ...defaults, ...params };

    if (!isValueSet(value)) {
        return null;
    }

    return moment(value).format(params.withTime ? "YYYY-MM-DD HH:mm:ssZZ" : "YYYY-MM-DD");
}

/**
 * Converts a date format used by date pickers to a format used by moment.js
 */
function toPickerDateFormat(momentFormat) {
    return momentFormat
        .replace(/Y/g, "y") // moment.js uses "Y" while the picker requires "y"
        .replace(/D/g, "d"); // moment.js uses "D" while the picker requires "d"
}

/**
 * Converts a date format used by moment.js to a format used by date pickers
 */
function toMomentDateFormat(defaultFormat) {
    return defaultFormat
        .replace(/y/g, "Y") // value format of picker uses "y" while moment.js uses "Y"
        .replace(/d/g, "D"); // value format of picker uses "d" while moment.js uses "D"
}

/**
 * Formats a money based amount based on the current user language.
 */
function formatAsMoneyAmount(value, params = {}) {
    const defaults = {
        showEmptyAsNotAvailable: false,
        showZeroAsNotAvailable: false,
        currency: "",
        decimals: 2,
        percentValue: ""
    };
    params = { ...defaults, ...params};

    if (!isValueSet(value)) {
        return params.showEmptyAsNotAvailable ? localizedNotAvailableText : "";
    }

    if (value === 0 && params.showZeroAsNotAvailable) {
        return localizedNotAvailableText;
    }

    let currency = isValueSet(params.currency) ? params.currency : "";
    let decimals = isValueSet(params.decimals) ? params.decimals : 2;
    let formattedAmount = accounting.formatMoney(value, currency, decimals).trim();

    if (isValueSet(params.percentValue)) {
        formattedAmount += " (" + formatAsDecimal(params.percentValue, { zeroDefault: "0%", showPercentSign: true }) +")";
    }

    return formattedAmount;
}

/**
 * Formats a money amount for communication with the backend API.
 */
function formatAsMoneyAmountForAPI(value) {
	if (!isValueSet(value)) {
		return null;
	}

	return accounting.unformat(value)
}

/**
 * Formats a decimal value according to the user's language.
 */
function formatAsDecimal(value, params = {}) {
    const defaults = {
        showEmptyAsNotAvailable: false,
        showZeroAsNotAvailable: false,
        emptyDefault: "",
        zeroDefault: "",
        decimals: 2,
        showPercentSign: false,
    };
    params = { ...defaults, ...params };

    if (!isValueSet(value)) {
        return params.showEmptyAsNotAvailable ? localizedNotAvailableText : params.emptyDefault;
    }

    if (value === 0) {
        return params.showZeroAsNotAvailable ? localization.i18n.t("common.value.notAvailable") : params.zeroDefault;
    }

    let decimals = isValueSet(params.decimals) ? params.decimals : 2;
    let suffix = params.showPercentSign ? "%" : "";

    return accounting.formatMoney(value, "", decimals).trim() + suffix;
}
/**
 * Formats whatever the input is, as localized hidden value
 */
function formatAsHidden() {
    return localizedHiddenValue;
}


/**
 * Formats a decimal value as percentage by multiplying it with 100
 */
function formatAsPercentage(value, params = {}) {
    return formatAsDecimal(
        isValueSet(value) ? value * 100 : value,
        {
            ...params,
            showPercentSign: true
        });
}

/**
 * Formats an industry id as its localized text.
 */
function formatAsIndustryText(value, params = {}) {
    const defaults = {
        showEmptyAsNotAvailable: false,
        detailLevel: null, // null = render industry as is, otherwise it is rendered as the specified level according to enumDef.industryDetailLevel
    };
    params = { ...defaults, ...params };

    // Adjust value to level if required
    if (isValueSet(params.detailLevel)) {
        value = enumDef.industryDetailLevel.naceCodeForLevel(value, params.detailLevel)
    }

    if (!isValueSet(value)) {
        return params.showEmptyAsNotAvailable ? localizedNotAvailableText : "";
    }

    return localization.i18n.t("common.industry." + value);
}

/**
 * Formats a value as is or as localized not available text if it is not
 */
function formatAsIsOrNotAvailable(value) {
    if (!isValueSet(value)) {
        return localizedNotAvailableText;
    }

    return value;
}

/**
 * Formats a number with day/days behind
 */
function formatAsNumberWithDays(value, params = {}) {
    const defaults = {
        showEmptyAsNotAvailable: false,
        showZeroAsEmpty: true,
        decimals: 0,
    };
    params = { ...defaults, ...params };

    if (!isValueSet(value)) {
        return params.showEmptyAsNotAvailable ? localizedNotAvailableText : "";
    }

    let formattedValue = accounting.formatMoney(value, "", params.decimals).trim();

    if (value === 1 || value === -1) {
        return formattedValue + " " + localization.i18n.t("common.day");
    } else if (value === 0 ) {
        return params.showZeroAsEmpty ? "" : formattedValue + " " + localization.i18n.t("common.days");
    } else {
        return formattedValue + " " + localization.i18n.t("common.days");
    }
}

/**
 * Formats an enum as its localized text
 */
function formatAsLocalizedEnum(enumDef, value, params = {}) {
    const defaults = {
        showEmptyAsNotAvailable: false
    };

    params = { ...defaults, ...params };

    if (!isValueSet(value)) {
        return params.showEmptyAsNotAvailable ? localizedNotAvailableText : "";
    }

    // If no enum definition is given use the value as is
    if (!enumDef) {
        return value;
    }

    // If no definition for the specific value exists show it as unknown
    if (!enumDef.hasOwnProperty("properties") || !enumDef.properties.hasOwnProperty(value)) {
        return localization.i18n.t("common.data.unknown");
    }

    // everything fine, properly localize it
    return localization.i18n.t(enumDef.properties[value].key);
}

/**
 * Formats a country ISO code as name
 */
function formatAsCountryName(value) {
    return localization.i18n.t("common.country." + value);
}

/**
 * Formats single address parts into a single address string
 */
function formatAsAddress(street, houseNumber, postCode, city, country) {
    return street + " " + houseNumber + ", " + postCode + " " + city + ", " + formatAsCountryName(country);
}

/**
 * Formats boolean object as "true" or "false" string
 */
function formatAsBooleanString(boolean) {
    return boolean ? "true" : "false";
}

/**
 * Formats a text as HTML, e.g. replacing line breaks with "<br>"
 */
function formatAsHtml(text) {
    return text ? text.replace(/\r\n|\r|\n/gi, '<br>') : text;
}

/**
 * Formats a text as a single line, removing all line break styles
 */
function formatAsSingleTextLine(text) {
    return text ? text.replace(/\s*(\r\n|\r|\n|<br>|<\/br>|<br\/>)+\s*/gi, ' ') : text;
}

/**
 * Formats any undefined or null value as localized "n/a"
 */
function formatEmptyAsNotAvailable(value) {
    if (!isValueSet(value)) {
        return localizedNotAvailableText;
    }

    return value;
}

function isValueSet(value) {
    return value !== undefined && value !== null && value !== "";
}

function roundToScale(value, scale = 2) {
    return Number(new Big(value).round(scale).toString());
}
export {
    isValueSet,
	formatAsLocalizedDate,
	formatAsDateForAPI,
    toPickerDateFormat,
    toMomentDateFormat,
	formatAsMoneyAmount,
	formatAsMoneyAmountForAPI,
    formatAsDecimal,
    formatAsHidden,
    formatAsPercentage,
	formatAsIndustryText,
    formatAsIsOrNotAvailable,
    formatAsNumberWithDays,
    formatAsLocalizedEnum,
    formatAsCountryName,
    formatAsAddress,
    formatAsBooleanString,
    formatAsHtml,
    formatAsSingleTextLine,
    formatEmptyAsNotAvailable,
    roundToScale
}